import React, { useState, useEffect, useCallback, useRef } from 'react';

// עדכן את הכתובת ל-URL של ה-Firebase Function שלך
const API_URL = "https://cookfromvideo.onrender.com/api";

const VoiceCookingMode = ({ recipe, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [isSupported, setIsSupported] = useState(true);
  const [isAiThinking, setIsAiThinking] = useState(false);
  
  const recognitionRef = useRef(null);

  // חילוץ השלבים
  const steps = recipe.content.split('\n').filter(line => line.trim().length > 5);

  const speak = (text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'he-IL';
    utterance.rate = 0.9;
    
    // כשהדיבור מסתיים, חוזרים להקשיב
    utterance.onend = () => {
      if (!isAiThinking) startListening();
    };
    
    window.speechSynthesis.speak(utterance);
  };

  const askServerAI = async (question) => {
    setIsAiThinking(true);
    window.speechSynthesis.cancel(); // מפסיקים הכל אם ה-AI הולך לדבר
    
    try {
      const response = await fetch(`${API_URL}/api/ai/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userQuestion: question,
          recipeContent: recipe.content
        })
      });

      const data = await response.json();
      if (data.answer) {
        speak(data.answer);
      }
    } catch (error) {
      console.error("AI Error:", error);
      speak("סליחה, הייתה לי תקלה בחיבור לשרת");
    } finally {
      setIsAiThinking(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      speak(`שלב ${next + 1}: ${steps[next]}`);
    } else {
      speak("סיימנו את כל השלבים! בתיאבון שף");
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      speak(`חזרנו לשלב ${prev + 1}: ${steps[prev]}`);
    }
  };

  const startListening = useCallback(() => {
    if (isAiThinking) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
      return;
    }

    if (!recognitionRef.current) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.lang = 'he-IL';
      recognitionRef.current.continuous = false; // עדיף false כדי לעבד משפט משפט
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onstart = () => setIsListening(true);
      recognitionRef.current.onend = () => {
        setIsListening(false);
        // אם לא סגרנו את המוד באופן יזום ולא חושבים, פותחים שוב
        if (!isAiThinking) recognitionRef.current.start();
      };

      recognitionRef.current.onresult = (event) => {
        const command = event.results[0][0].transcript.toLowerCase();
        setTranscript(command);

        // 1. פקודות ניווט (צד לקוח)
        if (command.includes("הבא") || command.includes("קדימה")) {
          handleNext();
        } else if (command.includes("חזור") || command.includes("אחורה")) {
          handlePrev();
        } else if (command.includes("תקריא") || command.includes("שוב")) {
          speak(steps[currentStep]);
        } 
        // 2. שאלות חופשיות (צד שרת)
        else {
          recognitionRef.current.stop(); // עוצרים הקשבה בזמן שהשרת עונה
          askServerAI(command);
        }
      };
    }

    try {
        recognitionRef.current.start();
    } catch (e) {
        // מונע שגיאה אם כבר התחיל
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, steps, isAiThinking]);

  useEffect(() => {
    speak(`מתחילים לבשל. שלב ראשון: ${steps[0]}`);
    startListening();
    return () => {
        window.speechSynthesis.cancel();
        if (recognitionRef.current) recognitionRef.current.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isSupported) return <div className="p-10 text-center text-white">הדפדפן לא תומך בזיהוי קולי</div>;

  return (
    <div className="fixed inset-0 z-[100] bg-zinc-950 flex flex-col items-center justify-between p-8 text-white font-body">
      
      {/* Header */}
      <div className="w-full flex justify-between items-center">
        <button onClick={onClose} className="p-3 bg-zinc-900 rounded-full text-zinc-400">
          <span className="material-symbols-outlined">close</span>
        </button>
        <div className="flex items-center gap-2">
          <span className={`w-3 h-3 rounded-full ${isAiThinking ? 'bg-amber-500 animate-bounce' : isListening ? 'bg-primary animate-pulse' : 'bg-red-500'}`}></span>
          <span className="font-bold text-sm">
            {isAiThinking ? 'ה-AI חושב...' : isListening ? 'מקשיב לך...' : 'בדיקה...'}
          </span>
        </div>
      </div>

      {/* Main Display */}
      <div className="flex-1 flex flex-col items-center justify-center text-center max-w-2xl w-full">
        <span className="text-primary font-black text-sm mb-4 uppercase tracking-widest bg-primary/10 px-4 py-1 rounded-full">
          שלב {currentStep + 1} מתוך {steps.length}
        </span>
        
        <h2 className="text-3xl md:text-5xl font-headline font-extrabold leading-tight mb-10 min-h-[150px]">
          {steps[currentStep].replace(/[*#]/g, '')}
        </h2>

        {transcript && (
          <div className="bg-zinc-900 border border-white/5 px-6 py-4 rounded-2xl max-w-sm">
            <p className="text-zinc-500 text-xs mb-1 uppercase font-bold tracking-tighter text-right">שמעתי אותך אומר:</p>
            <p className="text-white text-lg italic text-right">"{transcript}"</p>
          </div>
        )}
      </div>

      {/* Footer Controls */}
      <div className="w-full max-w-md">
        <div className="flex justify-around items-center mb-8">
            <button onClick={handlePrev} className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center active:scale-90 transition-transform">
                <span className="material-symbols-outlined text-2xl text-zinc-400">arrow_forward</span>
            </button>

            <button onClick={() => speak(steps[currentStep])} className="w-20 h-20 rounded-full bg-primary flex items-center justify-center shadow-2xl shadow-primary/40 active:scale-95 transition-all">
                <span className="material-symbols-outlined text-3xl text-white">volume_up</span>
            </button>

            <button onClick={handleNext} className="w-16 h-16 rounded-full bg-zinc-900 flex items-center justify-center active:scale-90 transition-transform">
                <span className="material-symbols-outlined text-2xl text-zinc-400">arrow_back</span>
            </button>
        </div>
        
        <div className="text-center">
            <p className="text-zinc-500 text-sm font-medium">נסה לשאול: "כמה מלח צריך?"</p>
            <p className="text-zinc-600 text-xs mt-1">או אמור "הבא" לעבור שלב</p>
        </div>
      </div>
    </div>
  );
};

export default VoiceCookingMode;